import { useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { useAccountNavItems } from '../NavBar/account-nav';
import { useTakeoverEscape } from './useTakeoverEscape';
import { AccountShellView } from './AccountShellView';

/**
 * Layout route for the Account takeover: personal `/profile`, `/settings`,
 * and plugin-contributed user-menu routes (`/tutorials`, `/notifications`)
 * render inside this shell, and the app shell drops its sidebar on these
 * paths (see `isAccountPath`). Aside carries exactly the account menu
 * options — the same set the account dropdown exposes — plus the Back chip
 * with its `esc` hint. `esc` and Back return to the active org's agent
 * sessions page, matching the other takeover shells.
 *
 * Reached by clicking an account option in the user-menu dropdown, which
 * navigates here; the agent session list stops being the sidebar on account
 * surfaces as a result.
 */
export function AccountShell() {
  const { user } = useAuth();
  const slug = useActiveOrgSlug();
  const navigate = useNavigate();
  const location = useLocation();
  const items = useAccountNavItems();

  const goBack = useCallback(() => {
    // Account routes are root-level, so `useActiveOrgSlug` falls back to
    // activeMembership and finally the `_` sentinel. Never navigate to
    // `/orgs/_/runs` — OrgSlugResolver cannot resolve that slug.
    const fallback = slug && slug !== '_' ? ROUTES.org(slug).runs : ROUTES.root;
    void navigate(fallback);
  }, [navigate, slug]);

  useTakeoverEscape(goBack);

  return (
    <AccountShellView
      subtitle={user?.email ?? user?.login ?? 'Account'}
      activePath={location.pathname}
      items={items}
      onBack={goBack}
    >
      <Outlet />
    </AccountShellView>
  );
}
